import { lazy, ReactElement } from "react";
import { useSelector } from "react-redux";
import { PermissionGate } from "../components/admin/PermissionGate";
import { PermissionsKey } from "../data/permissions";
import { RootState } from "../redux/store";
import ProtectedRouteAdmin from "./ProtectedAdminRoute";
import PublicRouteAdmin from "./PublicRouteAdmin";
import StoreModeGuard from "./StoreModeGuard";
import CategoryOpeningHours from "../pages/store/category/CategoryOpeningHours";
import ProductOpeningHours from "../pages/store/products/ProductOpeningHours";

const AddExtraItems = lazy(() => import("../components/store/products/form/ExtraItemsForm"));
const AcceptedInvitesAdminPage = lazy(() => import("../pages/admin/acceptedInvites/AcceptedInvitesAdminPage"));
const AdminAuditLogsPage = lazy(() => import("../pages/admin/auditLogs/AdminAuditLogsPage"));
const AdminLoginPage = lazy(() => import("../pages/admin/auth/AdminLoginPage"));
const AdminSendOtp = lazy(() => import("../pages/admin/auth/AdminSendOptPage"));
const AdminVerifyOtp = lazy(() => import("../pages/admin/auth/AdminVerifyOtp"));
const AdminAddAvatarPage = lazy(() => import("../pages/admin/avatars/AdminAddAvatarPage"));
const AdminViewAllAvatarsPage = lazy(() => import("../pages/admin/avatars/AdminViewAllAvatarsPage"));
const AdminAddCarBrandPage = lazy(() => import("../pages/admin/carBrand/AdminAddCarBrandPage"));
const AdminViewAllCarBrandsPage = lazy(() => import("../pages/admin/carBrand/AdminViewAllCarBrandsPage"));
const ContactInfoAdminPage = lazy(() => import("../pages/admin/contactSystem/ContactInfoPage"));
const AdminAddCoupounPage = lazy(() => import("../pages/admin/coupouns/AdminAddCopounPage"));
const AdminUpdateCoupounPage = lazy(() => import("../pages/admin/coupouns/AdminUpdateCoupounPage"));
const AdminViewCoupouns = lazy(() => import("../pages/admin/coupouns/AdminViewCoupouns"));
const AdminViewStreaks = lazy(() => import("../pages/admin/streaks/AdminViewStreaks"));
const AdminAddStreakPage = lazy(() => import("../pages/admin/streaks/AdminAddStreakPage"));
const AdminUpdateStreakPage = lazy(() => import("../pages/admin/streaks/AdminUpdateStreakPage"));
const AdminStreakDetailsPage = lazy(() => import("../pages/admin/streaks/AdminStreakDetailsPage"));
const CustomAppPage = lazy(() => import("../pages/admin/customApp/CustomAppPage"));
const AdminViewCustomersPage = lazy(() => import("../pages/admin/customers/AdminViewCustomersPage"));
const CustomerDetailsPage = lazy(() => import("../pages/admin/customers/CustomerDetailsPageForAdmin"));
const AdminDonationsPage = lazy(() => import("../pages/admin/donations/AdminDonationsPage"));
const AdminViewReviewsPage = lazy(() => import("../pages/admin/reviews/AdminViewReviewsPage"));
const AdminViewAllCustomersGifts = lazy(() => import("../pages/admin/customersGifts/AdminViewAllCustomersGifts"));
const AdminBookingsPage = lazy(() => import("../pages/admin/bookings/AdminBookingsPage"));
const AdminAddNewEmployee = lazy(() => import("../pages/admin/employees/AdminAddNewEmployee"));
const AdminViewEmployees = lazy(() => import("../pages/admin/employees/AdminViewEmployees"));
const EmployeeActivityPageLogs = lazy(() => import("../pages/admin/employees/EmployeeActivityPageLogs"));
const AdminAddEventPage = lazy(() => import("../pages/admin/events/AdminAddEventPage"));
const AdminUpdateEventPage = lazy(() => import("../pages/admin/events/AdminUpdateEventPage"));
const AdminViewEventsPage = lazy(() => import("../pages/admin/events/AdminViewEventsPage"));
const AdminEventStoresPage = lazy(() => import("../pages/admin/events/AdminEventStoresPage"));
const AdminEventOwnersPage = lazy(() => import("../pages/admin/events/AdminEventOwnersPage"));
const AdminAddGiftCategoryPage = lazy(() => import("../pages/admin/giftCategories/AdminAddGiftCategoryPage"));
const AdminViewGiftCategoriesPage = lazy(() => import("../pages/admin/giftCategories/AdminViewGiftCategories"));
const AdminAddGiftTemplatePage = lazy(() => import("../pages/admin/giftTemplates/AdminAddGiftTemplate"));
const AdminViewGiftTemplatesPage = lazy(() => import("../pages/admin/giftTemplates/AdminViewGiftTemplates"));
const AdminHomePage = lazy(() => import("../pages/admin/home/AdminHomePage"));
const SharedAdminHomePage = lazy(() => import("../pages/admin/home/SharedAdminHomePage"));
const AdminCancelReasonsPage = lazy(() => import("../pages/admin/cancelReasons/AdminCancelReasonsPage"));
const AdmiinViewLoyaltyOffersPage = lazy(() => import("../pages/admin/loyaltyOffers/AdmiinViewLoyaltyOffersPage"));
const AdminAddLoyaltyOfferPage = lazy(() => import("../pages/admin/loyaltyOffers/AdminAddLoyaltyOfferPage"));
const AdminTopUpTransactionsPage = lazy(() => import("../pages/admin/loyalty/AdminTopUpTransactionsPage"));
const CustomerBonusesAdminPage = lazy(() => import("../pages/admin/loyalty/CustomerBonusesAdminPage"));
const CustomerRewardsAdminPage = lazy(() => import("../pages/admin/loyalty/CustomerRewardsAdminPage"));
const AdminCreateNotificationPage = lazy(() => import("../pages/admin/notifications/AdminCreateNotificationPage"));
const AdminNotificationsListPage = lazy(() => import("../pages/admin/notifications/AdminNotificationsListPage"));
const AdminSendSmsPage = lazy(() => import("../pages/admin/notifications/AdminSendSmsPage"));
const AdminSendSmsExcelPage = lazy(() => import("../pages/admin/notifications/AdminSendSmsExcelPage"));
const AdminNotificationTrackingPage = lazy(() => import("../pages/admin/notifications/AdminNotificationTrackingPage"));
const AdminAddOrganizerPage = lazy(() => import("../pages/admin/organizers/AdminAddOrganizerPage"));
const AdminViewOrganizersPage = lazy(() => import("../pages/admin/organizers/AdminViewOrganizersPage"));
const AdminOrganizationsPage = lazy(() => import("../pages/admin/organizations/AdminOrganizationsPage"));
const OrganizationContextPage = lazy(() => import("../pages/admin/organizations/OrganizationContextPage"));
const AdminMerchantRegistrationsPage = lazy(() => import("../pages/admin/merchantRegistrations/AdminMerchantRegistrationsPage"));
const AdminViewOtpServiceLogs = lazy(() => import("../pages/admin/otpService/AdminViewOtpServiceLogs"));
const AdminCreateOwnerPage = lazy(() => import("../pages/admin/owners/AdminCreateOwnerPage"));
const AdminViewOwnersPage = lazy(() => import("../pages/admin/owners/AdminViewOwnersPage"));
const OnwerDetailsPageForAdmin = lazy(() => import("../pages/admin/owners/OnweDetailsPageForAdmin"));
const AddPartnerPage = lazy(() => import("../pages/admin/partners/AddPartnerPage"));
const ViewPartnersPage = lazy(() => import("../pages/admin/partners/ViewPartnersPage"));
const PointsSystemAdminPage = lazy(() => import("../pages/admin/points/PointsSystemAdminPage"));
const RewardsSystemAdminPage = lazy(() => import("../pages/admin/points/RewardsSystemAdminPage"));
const AdminAddRole = lazy(() => import("../pages/admin/roles/AdminAddRole"));
const AdminUpdateRole = lazy(() => import("../pages/admin/roles/AdminUpdateRole"));
const AdminViewRolesPage = lazy(() => import("../pages/admin/roles/AdminViewRolesPage"));
const RoleDetailsPage = lazy(() => import("../pages/admin/roles/RoleDetailsPage"));
const AdminAddSectorPage = lazy(() => import("../pages/admin/sectors/AdminAddSectorPage"));
const AdminViewSectorsPage = lazy(() => import("../pages/admin/sectors/AdminViewAllSectorsPage"));
const AdminViewAllSignupRewardsPage = lazy(() => import("../pages/admin/SignupRewards/AdminViewAllSignupRewardsPage"));
const SiteContentPage = lazy(() => import("../pages/admin/siteContent/SiteContentPage"));
const CreateSocialLinkPage = lazy(() => import("../pages/admin/socialLinks/CreateSocialLink"));
const ViewSocialLinksPage = lazy(() => import("../pages/admin/socialLinks/ViewSocialLinksPage"));
const AdminViewStoresPage = lazy(() => import("../pages/admin/stores/AdminViewStoresPage"));
const StoreDetailPage = lazy(() => import("../pages/admin/stores/StoreDetailPage"));
const AdminStoreNotesPage = lazy(() => import("../pages/admin/stores/AdminStoreNotesPage"));
const ViewStoreActivityPage = lazy(() => import("../pages/admin/stores/ViewStoreActivityPage"));
const AdminAddStoreSubTypePage = lazy(() => import("../pages/admin/storeSubTypes/AddStoreSubType"));
const AdminViewStoreSubTypesPage = lazy(() => import("../pages/admin/storeSubTypes/AdminViewStoreSubTypesPage"));
const AdminProductPriceApprovalPage = lazy(() => import("../pages/admin/productPriceApproval/AdminProductPriceApprovalPage"));
const AdminAddStoreTypePage = lazy(() => import("../pages/admin/storeTypes/AdminAddStoreType"));
const AdminViewAllStoreTypesPage = lazy(() => import("../pages/admin/storeTypes/AdminViewAllStoreTypesPage"));
const AdminTopBrandsPage = lazy(() => import("../pages/admin/topBrands/AdminTopBrandsPage"));
const AdminAddVariantCategoryPage = lazy(() => import("../pages/admin/varaintCategories/AdminAddVariantCategoryPage"));
const AdminViewVariantCategoriesPage = lazy(() => import("../pages/admin/varaintCategories/AdminViewVariantCategoriesPage"));
const OwnerHomePage = lazy(() => import("../pages/owner/home/OwnerHomePage"));
const CreateStoreWizard = lazy(() => import("../pages/owner/store/CreateStoreWizard"));
const CreateCategoryPage = lazy(() => import("../pages/store/category/CreateCategoryPage"));
const StoreCategoriesOverView = lazy(() => import("../pages/store/category/StoreCategoriesOverView"));
const AddOfferPage = lazy(() => import("../pages/store/offers/CreateOfferPage"));
const StoreOffersOverView = lazy(() => import("../pages/store/offers/StoreOffersOverView"));
const OrderDetailsPage = lazy(() => import("../pages/store/orders/OrderDetailsPage"));
const StoreOrdersOverView = lazy(() => import("../pages/store/orders/StoreOrdersOverView"));
const PosCartViewScreen = lazy(() => import("../pages/store/pos/PosCartViewScreen"));
const AddProduct = lazy(() => import("../pages/store/products/AddProduct"));
const AddProductInstructions = lazy(() => import("../pages/store/products/AddProductInstructions"));
const AddProductVariant = lazy(() => import("../pages/store/products/AddProductVariant"));
const ProductDetailsPage = lazy(() => import("../pages/store/products/ProductDetailsPage"));
const StoreProductsOverView = lazy(() => import("../pages/store/products/StoreProductsOverView"));
const UpdateProduct = lazy(() => import("../pages/store/products/UpdateProduct"));
const UploadextraItemsPage = lazy(() => import("../pages/store/products/UploadExtraItems"));
const UploadInstructionsPage = lazy(() => import("../pages/store/products/UploadInstructions"));
const UploadProducts = lazy(() => import("../pages/store/products/UploadProducts"));
const UploadVariantsPage = lazy(() => import("../pages/store/products/UploadVariants"));
const CreateBookingServicePage = lazy(() => import("../pages/store/bookingServices/CreateBookingServicePage"));
const StoreBookingServicesOverView = lazy(() => import("../pages/store/bookingServices/StoreBookingServicesOverView"));
const StoreBookingDetailsPage = lazy(() => import("../pages/store/bookings/StoreBookingDetailsPage"));
const StoreReviewOverView = lazy(() => import("../pages/store/review/StoreReviewOverView"));
const StoreStaffOverView = lazy(() => import("../pages/store/staff/StoreStaffOverView"));
const StoreLaundryServicesOverView = lazy(() => import("../pages/store/laundryServices/StoreLaundryServicesOverView"));
const CreateLaundryServicePage = lazy(() => import("../pages/store/laundryServices/CreateLaundryServicePage"));
const LaundryServiceDetailsPage = lazy(() => import("../pages/store/laundryServices/LaundryServiceDetailsPage"));
const StoreOverviewTransactions = lazy(() => import("../pages/store/transactions/StoreOverviewTransactions"));
const UpdateStore = lazy(() => import("../pages/store/updatePages.tsx/UpdateStore"));
const StoreAddVariantCategoryPage = lazy(() => import("../pages/store/variantCategories/StoreAddVariantCategory"));
const StoreViewVariantCategoriesPage = lazy(() => import("../pages/store/variantCategories/StoreViewVariantCategories"));
const AdminOrdersPage = lazy(() => import("../pages/admin/orders/AdminOrdersPage"));
const AdminContractDetailsPage = lazy(() => import("../pages/admin/contracts/AdminContractDetailsPage"));
const AdminContractsPage = lazy(() => import("../pages/admin/contracts/AdminContractsPage"));
const AdminOffersOverView = lazy(() => import("../pages/admin/offers/AdminOffersOverView"));
const AdminOfferDetailsPage = lazy(() => import("../pages/admin/offers/AdminOfferDetailsPage"));
const AdminFoodicsPage = lazy(() => import("../features/foodics/pages/AdminFoodicsPage"));
const AdminBlendPage = lazy(() => import("../features/blend/pages/AdminBlendPage"));
const AdminFoodicsOwnersPage = lazy(() => import("../features/foodics/pages/AdminFoodicsOwnersPage"));
const AdminFoodizoneOwnersPage = lazy(() => import("../features/foodizone/pages/AdminFoodizoneOwnersPage"));
const AdminDeliverectOwnersPage = lazy(() => import("../features/foodics/pages/AdminDeliverectOwnersPage"));
const AdminOwnerFoodizoneIntegrationPage = lazy(() => import("../features/foodizone/pages/AdminOwnerFoodizoneIntegrationPage"));
const AdminPrizeWheelPage = lazy(() => import("../pages/admin/prizeWheel/AdminPrizeWheelPage"));
const AdminPrizeWheelSpinsPage = lazy(() => import("../pages/admin/prizeWheel/AdminPrizeWheelSpinsPage"));
const SuggestStoresPage = lazy(() => import("../features/store-suggestions/pages/suggestStoresPage"));
const AdminViewOfferBanners = lazy(() => import("../../src/features/offer-banner/pages/AdminViewOfferBanners"));
const TicketTypesPage = lazy(() => import("../pages/admin/tickets/TicketTypesPage"));
const ViewTicketsPage = lazy(() => import("../pages/admin/tickets/ViewTicketsPage"));
const AdminLaundryOrdersOverView = lazy(() => import("../pages/admin/laundry/LaundryOrdersOverView"));
const AdminLaundryOrderDetailsPage = lazy(() => import("../pages/admin/laundry/LaundryOrderDetailsPage"));
const AdminCartPage = lazy(() => import("../pages/admin/cart/AdminCartPage"));
const AdminCartPreviewPage = lazy(() => import("../shared/cart/pages/CartPreviewRoutePage"));
const AdminNotificationTemplatesPage = lazy(() => import("../pages/admin/notifications/AdminNotificationTemplatesPage"));
const ReorderProductsAndCategories = lazy(() => import("../pages/store/products/ReorderProductsAndCategories"));
const AdminStreakCustomerProgressPage = lazy(() => import("../pages/admin/streaks/AdminStreakCustomerProgressPage"));
const AdminAddCustomerBalancePage = lazy(() => import("../pages/admin/customers/AdminAddCustomerBalancePage"));
const StoreOrdersMapPage = lazy(() => import("../pages/store/orders/StoreOrdersMapPage"));

const withOrderFeatureGuard = (element: ReactElement) => (
    <StoreModeGuard requireOrderFeatures>{element}</StoreModeGuard>
);

const withPosFeatureGuard = (element: ReactElement) => (
    <StoreModeGuard requireOrderFeatures requirePosFeatures>{element}</StoreModeGuard>
);

const withBookingFeatureGuard = (element: ReactElement) => (
    <StoreModeGuard requireBookingFeatures>{element}</StoreModeGuard>
);

const withLaundryFeatureGuard = (element: ReactElement) => (
    <StoreModeGuard requireLaundryFeatures>{element}</StoreModeGuard>
);

const AdminHomeRoute = () => {
    const permissions = useSelector((state: RootState) => {
        if (state.auth.permissions?.length) return state.auth.permissions;
        return state.admin.permissions;
    });

    const hasDashboardStatsPermission = permissions.includes(PermissionsKey.ViewDashboardStats);

    return hasDashboardStatsPermission ? <AdminHomePage /> : <SharedAdminHomePage />;
};

export const adminRoutes = [
    { path: "/admin", element: <ProtectedRouteAdmin><AdminHomeRoute /></ProtectedRouteAdmin> },
    { path: "/admin/dashboard", element: <ProtectedRouteAdmin><AdminHomeRoute /></ProtectedRouteAdmin> },

    { path: "/admin/login", element: <><AdminLoginPage /></> },
    { path: "/admin/send-otp", element: <PublicRouteAdmin><AdminSendOtp /></PublicRouteAdmin> },
    { path: "/admin/verify-otp", element: <PublicRouteAdmin><AdminVerifyOtp /></PublicRouteAdmin> },

    // admin all orders pages
    {
        path: "/admin/orders", element: <ProtectedRouteAdmin>
            <PermissionGate isPage permissions={[PermissionsKey.ViewStoreOrders]}>
                <AdminOrdersPage />
            </PermissionGate>
        </ProtectedRouteAdmin>
    }
    ,
    {
        path: "/admin/contracts", element: <ProtectedRouteAdmin>
            <PermissionGate isPage permissions={[PermissionsKey.ManageContract]}>
                <AdminContractsPage />
            </PermissionGate>
        </ProtectedRouteAdmin>
    },
    {
        path: "/admin/contracts/:contractId", element: <ProtectedRouteAdmin>
            <PermissionGate isPage permissions={[PermissionsKey.ManageContract]}>
                <AdminContractDetailsPage />
            </PermissionGate>
        </ProtectedRouteAdmin>
    },
    {
        path: "/admin/stores/:storeId/orders/map",
        element: (
            <ProtectedRouteAdmin>
                <PermissionGate
                    isPage
                    permissions={[PermissionsKey.ViewStoreOrders]}
                >
                    {withOrderFeatureGuard(<StoreOrdersMapPage />)}
                </PermissionGate>
            </ProtectedRouteAdmin>
        ),
    },
    {
        path: "/admin/bookings", element: <ProtectedRouteAdmin>
            <PermissionGate isPage permissions={[PermissionsKey.ViewStoreOrders]}>
                <AdminBookingsPage />
            </PermissionGate>
        </ProtectedRouteAdmin>
    }
    ,

    {
        path: "/admin/merchant-registrations", element: <ProtectedRouteAdmin>
            <PermissionGate isPage permissions={[PermissionsKey.ManageMerchantRegistration]}>
                <AdminMerchantRegistrationsPage />
            </PermissionGate>
        </ProtectedRouteAdmin>
    }
    ,

    {
        path: "/admin/laundry", element: <ProtectedRouteAdmin>
            <PermissionGate isPage permissions={[PermissionsKey.ManageLaundryOrder]}>
                <AdminLaundryOrdersOverView />
            </PermissionGate>
        </ProtectedRouteAdmin>
    }
    ,

    {
        path: "/admin/laundry/:orderId", element: <ProtectedRouteAdmin>
            <PermissionGate isPage permissions={[PermissionsKey.ManageLaundryOrder]}>
                <AdminLaundryOrderDetailsPage />
            </PermissionGate>
        </ProtectedRouteAdmin>
    }
    ,

    {
        path: "/admin/product-price-approval", element: <ProtectedRouteAdmin>
            <PermissionGate isPage permissions={[PermissionsKey.UpdateProduct]}>
                <AdminProductPriceApprovalPage />
            </PermissionGate>
        </ProtectedRouteAdmin>
    }
    ,

    {
        path: "/admin/cart", element: <ProtectedRouteAdmin>
            <PermissionGate isPage permissions={[PermissionsKey.ViewCart]}>
                <AdminCartPage />
            </PermissionGate>
        </ProtectedRouteAdmin>
    }
    ,
    {
        path: "/admin/cart/preview", element: <ProtectedRouteAdmin>
            <PermissionGate isPage permissions={[PermissionsKey.ViewCart]}>
                <AdminCartPreviewPage role="admin" isAdmin />
            </PermissionGate>
        </ProtectedRouteAdmin>
    }
    ,
    {
        path: "/admin/stores/:storeId/cart/preview", element: <ProtectedRouteAdmin>
            <PermissionGate isPage permissions={[PermissionsKey.ViewCart]}>
                <AdminCartPreviewPage role="admin" isAdmin />
            </PermissionGate>
        </ProtectedRouteAdmin>
    }
    ,

    {
        path: "/admin/offers", element: <ProtectedRouteAdmin>
            <PermissionGate isPage permissions={[PermissionsKey.ViewOffer]}>
                <AdminOffersOverView />
            </PermissionGate>
        </ProtectedRouteAdmin>
    }
    ,

    {
        path: "/admin/offers/:offerId", element: <ProtectedRouteAdmin>
            <PermissionGate isPage permissions={[PermissionsKey.ViewOffer]}>
                <AdminOfferDetailsPage />
            </PermissionGate>
        </ProtectedRouteAdmin>
    }
    ,

    {
        path: "/admin/tickets", element: <ProtectedRouteAdmin>
            <PermissionGate isPage permissions={[PermissionsKey.ManageTickets]}>
                <TicketTypesPage />
            </PermissionGate>
        </ProtectedRouteAdmin>
    }
    ,

    {
        path: "/admin/support-tickets", element: <ProtectedRouteAdmin>
            <PermissionGate isPage permissions={[PermissionsKey.ManageTickets]}>
                <ViewTicketsPage />
            </PermissionGate>
        </ProtectedRouteAdmin>
    }
    ,

    {
        path: "/admin/foodics", element: <ProtectedRouteAdmin>
            <PermissionGate isPage permissions={[PermissionsKey.SyncIntegrations]}>
                <AdminFoodicsPage />
            </PermissionGate>
        </ProtectedRouteAdmin>
    }
    ,
    {
        path: "/admin/foodics/owners", element: <ProtectedRouteAdmin>
            <PermissionGate isPage permissions={[PermissionsKey.SyncIntegrations]}>
                <AdminFoodicsOwnersPage />
            </PermissionGate>
        </ProtectedRouteAdmin>
    }
    ,
    {
        path: "/admin/blend", element: <ProtectedRouteAdmin>
            <PermissionGate isPage permissions={[PermissionsKey.SyncIntegrations]}>
                <AdminBlendPage />
            </PermissionGate>
        </ProtectedRouteAdmin>
    }
    ,
    {
        path: "/admin/foodizone", element: <ProtectedRouteAdmin>
            <PermissionGate isPage permissions={[PermissionsKey.SyncIntegrations]}>
                <AdminFoodizoneOwnersPage />
            </PermissionGate>
        </ProtectedRouteAdmin>
    }
    ,
    {
        path: "/admin/deliverect", element: <ProtectedRouteAdmin>
            <PermissionGate isPage permissions={[PermissionsKey.SyncIntegrations]}>
                <AdminDeliverectOwnersPage />
            </PermissionGate>
        </ProtectedRouteAdmin>
    }
    ,
    {
        path: "/admin/foodizone/:ownerId/sync", element: <ProtectedRouteAdmin>
            <PermissionGate isPage permissions={[PermissionsKey.SyncIntegrations]}>
                <AdminOwnerFoodizoneIntegrationPage />
            </PermissionGate>
        </ProtectedRouteAdmin>
    }
    ,
    {
        path: "/admin/events/:eventId/organizers/:organizerId/owners", element: <ProtectedRouteAdmin>
            <PermissionGate isPage permissions={[PermissionsKey.ViewEvent]}>
                <AdminEventOwnersPage />
            </PermissionGate>
        </ProtectedRouteAdmin>
    },


    // admin car brand pages
    {
        path: "/admin/car-brands", element: <ProtectedRouteAdmin>
            <PermissionGate isPage permissions={[PermissionsKey.ManageSyestemEntities]}><AdminViewAllCarBrandsPage /></PermissionGate>
        </ProtectedRouteAdmin>
    },
    {
        path: "/admin/add-carBrand", element: <ProtectedRouteAdmin>
            <PermissionGate isPage permissions={[PermissionsKey.ManageSyestemEntities]}>
                <AdminAddCarBrandPage />
            </PermissionGate>
        </ProtectedRouteAdmin>
    },


    // admin cancel reasons page
    {
        path: "/admin/cancel-reasons",
        element: (
            <ProtectedRouteAdmin>
                <PermissionGate
                    isPage
                    permissions={[PermissionsKey.ManageSyestemEntities]}
                >
                    <AdminCancelReasonsPage />
                </PermissionGate>
            </ProtectedRouteAdmin>
        ),
    }
    ,

    // admin store types page
    {
        path: "/admin/store-types", element: <ProtectedRouteAdmin>
            <PermissionGate isPage permissions={[PermissionsKey.ManageSyestemEntities]}>
                <AdminViewAllStoreTypesPage />
            </PermissionGate>
        </ProtectedRouteAdmin>
    },
    {
        path: "/admin/add-storeType", element: <ProtectedRouteAdmin>
            <PermissionGate permissions={[PermissionsKey.ManageSyestemEntities]}>
                <AdminAddStoreTypePage />
            </PermissionGate>
        </ProtectedRouteAdmin>
    },

    // admin top brands page
    {
        path: "/admin/top-brands", element: <ProtectedRouteAdmin>
            <PermissionGate isPage permissions={[PermissionsKey.ManageSyestemEntities]}>
                <AdminTopBrandsPage />
            </PermissionGate>
        </ProtectedRouteAdmin>
    },

    {
        path: "/admin/prize-wheel", element: <ProtectedRouteAdmin>
            <PermissionGate isPage permissions={[PermissionsKey.ManagePrizeWheel]}>
                <AdminPrizeWheelPage />
            </PermissionGate>
        </ProtectedRouteAdmin>
    },
    {
        path: "/admin/prize-wheel/spins", element: <ProtectedRouteAdmin>
            <PermissionGate isPage permissions={[PermissionsKey.ManagePrizeWheel]}>
                <AdminPrizeWheelSpinsPage />
            </PermissionGate>
        </ProtectedRouteAdmin>
    },

    // admin store sub types pages
    {
        path: "/admin/subTypes/:typeId", element: <ProtectedRouteAdmin>
            <PermissionGate isPage permissions={[PermissionsKey.ManageSyestemEntities]}>
                <AdminViewStoreSubTypesPage />
            </PermissionGate>
        </ProtectedRouteAdmin>
    },
    {
        path: "/admin/add-subType/:typeId", element: <ProtectedRouteAdmin>
            <PermissionGate isPage permissions={[PermissionsKey.ManageSyestemEntities]}>
                <AdminAddStoreSubTypePage />
            </PermissionGate>
        </ProtectedRouteAdmin>
    },

    //  admin avatars pages
    {
        path: "/admin/avatars", element: <ProtectedRouteAdmin>
            <PermissionGate isPage permissions={[PermissionsKey.ManageSyestemEntities]}>
                <AdminViewAllAvatarsPage />
            </PermissionGate>
        </ProtectedRouteAdmin>
    },
    {
        path: "/admin/add-avatar", element: <ProtectedRouteAdmin>
            <PermissionGate permissions={[PermissionsKey.ManageSyestemEntities]}>
                <AdminAddAvatarPage />
            </PermissionGate>
        </ProtectedRouteAdmin>
    },


    // admin sectors pages
    {
        path: "/admin/sectors", element: <ProtectedRouteAdmin>
            <PermissionGate isPage permissions={[PermissionsKey.ManageSyestemEntities]}>
                <AdminViewSectorsPage />
            </PermissionGate>
        </ProtectedRouteAdmin>
    },
    {
        path: "/admin/add-sector", element: <ProtectedRouteAdmin>
            <PermissionGate permissions={[PermissionsKey.ManageSyestemEntities]}>
                <AdminAddSectorPage />
            </PermissionGate>
        </ProtectedRouteAdmin>
    },

    // admin gift categories
    {
        path: "/admin/gift-categories", element: <ProtectedRouteAdmin>
            <PermissionGate isPage permissions={[PermissionsKey.ManageSyestemEntities]}>
                <AdminViewGiftCategoriesPage />
            </PermissionGate>
        </ProtectedRouteAdmin>
    },
    {
        path: "/admin/add-giftCategory", element: <ProtectedRouteAdmin>
            <PermissionGate isPage permissions={[PermissionsKey.ManageSyestemEntities]}>
                <AdminAddGiftCategoryPage />
            </PermissionGate>
        </ProtectedRouteAdmin>
    },
    //  gift templates for admin
    {
        path: "/admin/gift-templates/:giftCategoryId", element: <ProtectedRouteAdmin>
            <PermissionGate isPage permissions={[PermissionsKey.ManageSyestemEntities]}>
                <AdminViewGiftTemplatesPage />
            </PermissionGate>
        </ProtectedRouteAdmin>
    },
    {
        path: "/admin/add-giftTemplate/:giftCategoryId", element: <ProtectedRouteAdmin>
            <PermissionGate isPage permissions={[PermissionsKey.ManageSyestemEntities]}>
                <AdminAddGiftTemplatePage />
            </PermissionGate>
        </ProtectedRouteAdmin>
    },

    // admin variants categories pages
    {
        path: "/admin/variant-categories", element: <ProtectedRouteAdmin>
            <PermissionGate isPage permissions={[PermissionsKey.ManageSyestemEntities]}>
                <AdminViewVariantCategoriesPage />
            </PermissionGate>
        </ProtectedRouteAdmin>
    },
    {
        path: "/admin/add-variantCategory", element: <ProtectedRouteAdmin>
            <PermissionGate isPage permissions={[PermissionsKey.ManageSyestemEntities]}>
                <AdminAddVariantCategoryPage />
            </PermissionGate>
        </ProtectedRouteAdmin>
    },


    {
        path: "/admin/wallet", element: <ProtectedRouteAdmin>
            <PermissionGate isPage permissions={[PermissionsKey.ViewLoyaltyOffer]}>
                <AdmiinViewLoyaltyOffersPage />
            </PermissionGate>
        </ProtectedRouteAdmin>
    },
    {
        path: "/admin/add-wallet", element: <ProtectedRouteAdmin>
            <PermissionGate isPage permissions={[PermissionsKey.CreateLoyaltyOffer]}>
                <AdminAddLoyaltyOfferPage />
            </PermissionGate>
        </ProtectedRouteAdmin>
    },
    {
        path: "/admin/wallet-top-up-transactions", element: <ProtectedRouteAdmin>
            <PermissionGate isPage permissions={[PermissionsKey.ViewCustomerTransactions]}>
                <AdminTopUpTransactionsPage />
            </PermissionGate>
        </ProtectedRouteAdmin>
    },
    {
        path: "/admin/customer-bonuses", element: <ProtectedRouteAdmin>
            <PermissionGate isPage permissions={[PermissionsKey.ViewCustomerLoyaltyActivities]}>
                <CustomerBonusesAdminPage />
            </PermissionGate>
        </ProtectedRouteAdmin>
    },
    {
        path: "/admin/customer-rewards", element: <ProtectedRouteAdmin>
            <PermissionGate isPage permissions={[PermissionsKey.ViewCustomerLoyaltyActivities]}>
                <CustomerRewardsAdminPage />
            </PermissionGate>
        </ProtectedRouteAdmin>
    },

    // coupouns page for admin 
    {
        path: "/admin/coupouns", element: <ProtectedRouteAdmin>
            <PermissionGate isPage permissions={[PermissionsKey.ViewCoupon]}>
                <AdminViewCoupouns />
            </PermissionGate>
        </ProtectedRouteAdmin>
    },
    {
        path: "/admin/add-coupoun", element: <ProtectedRouteAdmin>
            <PermissionGate isPage permissions={[PermissionsKey.CreateCoupon]}>
                <AdminAddCoupounPage />
            </PermissionGate>
        </ProtectedRouteAdmin>
    },
    {
        path: "/admin/coupouns/:coupounId/update", element: <ProtectedRouteAdmin>
            <PermissionGate isPage permissions={[PermissionsKey.UpdateCoupon]}>
                <AdminUpdateCoupounPage />
            </PermissionGate>
        </ProtectedRouteAdmin>
    },

    // streaks page for admin
    {
        path: "/admin/streaks", element: <ProtectedRouteAdmin>
            <PermissionGate isPage permissions={[PermissionsKey.ManageStreak]}>
                <AdminViewStreaks />
            </PermissionGate>
        </ProtectedRouteAdmin>
    },
    {
        path: "/admin/streaks/:streakId/customer-progress",
        element: (
            <ProtectedRouteAdmin>
                <PermissionGate
                    isPage
                    permissions={[PermissionsKey.ManageStreak]}
                >
                    <AdminStreakCustomerProgressPage />
                </PermissionGate>
            </ProtectedRouteAdmin>
        ),
    },
    {
        path: "/admin/add-streak", element: <ProtectedRouteAdmin>
            <PermissionGate isPage permissions={[PermissionsKey.ManageStreak]}>
                <AdminAddStreakPage />
            </PermissionGate>
        </ProtectedRouteAdmin>
    },
    {
        path: "/admin/streaks/:streakId/update", element: <ProtectedRouteAdmin>
            <PermissionGate isPage permissions={[PermissionsKey.ManageStreak]}>
                <AdminUpdateStreakPage />
            </PermissionGate>
        </ProtectedRouteAdmin>
    },
    {
        path: "/admin/streaks/:streakId", element: <ProtectedRouteAdmin>
            <PermissionGate isPage permissions={[PermissionsKey.ManageStreak]}>
                <AdminStreakDetailsPage />
            </PermissionGate>
        </ProtectedRouteAdmin>
    },
    {
        path: "/admin/offer-banners", element: <ProtectedRouteAdmin>
            <PermissionGate isPage permissions={[PermissionsKey.ManageSyestemEntities]}>
                <AdminViewOfferBanners />
            </PermissionGate>
        </ProtectedRouteAdmin>
    },

    {
        path: "/admin/store-suggestions", element: <ProtectedRouteAdmin>
            <PermissionGate isPage permissions={[PermissionsKey.ManageSyestemEntities]}>
                <SuggestStoresPage />
            </PermissionGate>
        </ProtectedRouteAdmin>
    },

    {
        path: "/admin/signup-rewards", element: <ProtectedRouteAdmin>
            <PermissionGate isPage permissions={[PermissionsKey.ViewCustomerLoyaltyActivities]}>
                <AdminViewAllSignupRewardsPage />
            </PermissionGate>
        </ProtectedRouteAdmin>
    },

    {
        path: "/admin/accepted-invites", element: <ProtectedRouteAdmin>
            <PermissionGate isPage permissions={[PermissionsKey.ViewCustomerLoyaltyActivities]}>
                <AcceptedInvitesAdminPage />
            </PermissionGate>
        </ProtectedRouteAdmin>
    },


    // admin pages 
    {
        path: "/admin/gifts", element: <ProtectedRouteAdmin>
            <PermissionGate isPage permissions={[PermissionsKey.ViewCustomerGifts]}>
                <AdminViewAllCustomersGifts />
            </PermissionGate>
        </ProtectedRouteAdmin>
    },


    // stores pages for admin
    {
        path: "/admin/stores", element: <ProtectedRouteAdmin>
            <PermissionGate isPage permissions={[PermissionsKey.ViewStores]}>
                <AdminViewStoresPage />
            </PermissionGate>
        </ProtectedRouteAdmin>
    },
    {
        path: "/admin/organizations/:organizationId", element: <ProtectedRouteAdmin>
            <PermissionGate isPage permissions={[PermissionsKey.ManageOrganization]}>
                <OrganizationContextPage />
            </PermissionGate>
        </ProtectedRouteAdmin>
    },
    {
        path: "/admin/stores/:storeId", element: <ProtectedRouteAdmin>
            <PermissionGate isPage permissions={[PermissionsKey.ViewStores]}>
                <StoreDetailPage />
            </PermissionGate>
        </ProtectedRouteAdmin>
    },
    {
        path: "/admin/stores/:storeId/cart", element: <ProtectedRouteAdmin>
            <PermissionGate isPage permissions={[PermissionsKey.ViewCart]}>
                <StoreDetailPage />
            </PermissionGate>
        </ProtectedRouteAdmin>
    },
    {
        path: "/admin/stores/:storeId/settings", element: <ProtectedRouteAdmin>
            <PermissionGate isPage permissions={[PermissionsKey.UpdateStore]}>
                <UpdateStore />
            </PermissionGate>
        </ProtectedRouteAdmin>
    },
    {
        path: "/admin/stores/:storeId/notes", element: <ProtectedRouteAdmin>
            <PermissionGate isPage permissions={[PermissionsKey.ViewStores]}>
                <AdminStoreNotesPage />
            </PermissionGate>
        </ProtectedRouteAdmin>
    },
    {
        path: "/admin/stores/:storeId/bookings/:bookingId", element: <ProtectedRouteAdmin>
            <PermissionGate isPage permissions={[PermissionsKey.ViewStoreOrders]}>
                {withBookingFeatureGuard(<StoreBookingDetailsPage />)}
            </PermissionGate>
        </ProtectedRouteAdmin>
    },

    // admin products store pages
    {
        path: "/admin/stores/:storeId/products", element: <ProtectedRouteAdmin>
            <PermissionGate isPage permissions={[PermissionsKey.ViewProduct]}>
                {withOrderFeatureGuard(<StoreProductsOverView />)}
            </PermissionGate>
        </ProtectedRouteAdmin>
    },
    {
        path: "/admin/stores/:storeId/reorder-products-categories",
        element: (
            <ProtectedRouteAdmin>
                <PermissionGate
                    isPage
                    permissions={[
                        PermissionsKey.ViewProduct,
                        PermissionsKey.ViewStoreCategory,
                    ]}
                >
                    {withOrderFeatureGuard(
                        <ReorderProductsAndCategories />
                    )}
                </PermissionGate>
            </ProtectedRouteAdmin>
        ),
    },
    {
        path: "/admin/stores/:storeId/add-product", element: <ProtectedRouteAdmin>
            <PermissionGate isPage permissions={[PermissionsKey.CreateProduct]}>
                {withOrderFeatureGuard(<AddProduct />)}
            </PermissionGate>
        </ProtectedRouteAdmin>
    },
    {
        path: "/admin/stores/:storeId/upload-product", element: <ProtectedRouteAdmin>
            <PermissionGate isPage permissions={[PermissionsKey.CreateProduct]}>
                {withOrderFeatureGuard(<UploadProducts />)}
            </PermissionGate>
        </ProtectedRouteAdmin>
    },
    {
        path: "/admin/stores/:storeId/update-product/:productId", element: <ProtectedRouteAdmin>
            <PermissionGate isPage permissions={[PermissionsKey.UpdateProduct]}>
                {withOrderFeatureGuard(<UpdateProduct />)}
            </PermissionGate>
        </ProtectedRouteAdmin>
    },
    {
        path: "/admin/stores/:storeId/products/:productId/extra-items", element: <ProtectedRouteAdmin>
            <PermissionGate isPage permissions={[PermissionsKey.UpdateProduct, PermissionsKey.CreateProduct]}>
                {withOrderFeatureGuard(<AddExtraItems />)}
            </PermissionGate>
        </ProtectedRouteAdmin>
    },
    {
        path: "/admin/stores/:storeId/upload-extras", element: <ProtectedRouteAdmin>
            <PermissionGate isPage permissions={[PermissionsKey.CreateProduct]}>
                {withOrderFeatureGuard(<UploadextraItemsPage />)}
            </PermissionGate>
        </ProtectedRouteAdmin>
    },
    {
        path: "/admin/stores/:storeId/products/:productId/instructions", element: <ProtectedRouteAdmin>
            <PermissionGate isPage permissions={[PermissionsKey.CreateProduct, PermissionsKey.UpdateProduct]}>
                {withOrderFeatureGuard(<AddProductInstructions />)}
            </PermissionGate>
        </ProtectedRouteAdmin>
    },
    {
        path: "/admin/stores/:storeId/upload-instructions", element: <ProtectedRouteAdmin>
            <PermissionGate isPage permissions={[PermissionsKey.CreateProduct]}>
                {withOrderFeatureGuard(<UploadInstructionsPage />)}
            </PermissionGate>
        </ProtectedRouteAdmin>
    },
    {
        path: "/admin/stores/:storeId/products/:productId/variants", element: <ProtectedRouteAdmin>
            <PermissionGate isPage permissions={[PermissionsKey.UpdateProduct, PermissionsKey.CreateProduct]}>
                {withOrderFeatureGuard(<AddProductVariant />)}
            </PermissionGate>
        </ProtectedRouteAdmin>
    },
    {
        path: "/admin/stores/:storeId/upload-variants", element: <ProtectedRouteAdmin>
            <PermissionGate isPage permissions={[PermissionsKey.CreateProduct]}>
                {withOrderFeatureGuard(<UploadVariantsPage />)}
            </PermissionGate>
        </ProtectedRouteAdmin>
    },
    {
        path: "/admin/stores/:storeId/products/:productId", element: <ProtectedRouteAdmin>
            <PermissionGate isPage permissions={[PermissionsKey.ViewProduct]}>
                {withOrderFeatureGuard(<ProductDetailsPage />)}
            </PermissionGate>
        </ProtectedRouteAdmin>
    },

    {
        path: "/admin/stores/:storeId/products/:productId/opening-hours",
        element: (
            <ProtectedRouteAdmin>
                <PermissionGate
                    isPage
                    permissions={[
                        PermissionsKey.ViewProduct,
                    ]}
                >
                    {withOrderFeatureGuard(<ProductOpeningHours />)}
                </PermissionGate>
            </ProtectedRouteAdmin>
        ),
    },

    // admin add catgeory for store products
    {
        path: "/admin/stores/:storeId/categories", element: <ProtectedRouteAdmin>
            <PermissionGate isPage permissions={[PermissionsKey.ViewStoreCategory]}>
                {withOrderFeatureGuard(<StoreCategoriesOverView />)}
            </PermissionGate>
        </ProtectedRouteAdmin>
    },
    {
        path: "/admin/stores/:storeId/add-category", element: <ProtectedRouteAdmin>
            <PermissionGate isPage permissions={[PermissionsKey.CreateStoreCategory]}>
                {withOrderFeatureGuard(<CreateCategoryPage />)}
            </PermissionGate>
        </ProtectedRouteAdmin>
    },

    {
        path: "/admin/stores/:storeId/categories/:categoryId/opening-hours",
        element: (
            <ProtectedRouteAdmin>
                <PermissionGate
                    isPage
                    permissions={[
                        PermissionsKey.ViewStoreCategory,
                    ]}
                >
                    {withOrderFeatureGuard(<CategoryOpeningHours />,)}
                </PermissionGate>
            </ProtectedRouteAdmin>
        ),
    },

    // admin offers pages for store
    {
        path: "/admin/stores/:storeId/offers", element: <ProtectedRouteAdmin>
            <PermissionGate isPage permissions={[PermissionsKey.ViewOffer]}>
                <StoreOffersOverView />
            </PermissionGate>
        </ProtectedRouteAdmin>
    },
    {
        path: "/admin/stores/:storeId/offers/add-offer", element: <ProtectedRouteAdmin>
            <PermissionGate isPage permissions={[PermissionsKey.CreateOffer]}>
                <AddOfferPage />
            </PermissionGate>
        </ProtectedRouteAdmin>
    },

    // admin reviews pages for store
    {
        path: "/admin/stores/:storeId/reviews", element: <ProtectedRouteAdmin>
            <PermissionGate isPage permissions={[PermissionsKey.ViewStoreReviews]}>
                <StoreReviewOverView />
            </PermissionGate>
        </ProtectedRouteAdmin>
    },

    // admin transactions for store
    {
        path: "/admin/stores/:storeId/transactions", element: <ProtectedRouteAdmin>
            <PermissionGate isPage permissions={[PermissionsKey.ViewStoreTransactions]}>
                <StoreOverviewTransactions />
            </PermissionGate>
        </ProtectedRouteAdmin>
    },

    // admin staff page for store
    {
        path: "/admin/stores/:storeId/staff", element: <ProtectedRouteAdmin>
            <PermissionGate isPage permissions={[PermissionsKey.ManageStoreStaff]}>
                {withBookingFeatureGuard(<StoreStaffOverView />)}
            </PermissionGate>
        </ProtectedRouteAdmin>
    },

    {
        path: "/admin/stores/:storeId/booking-services", element: <ProtectedRouteAdmin>
            <PermissionGate isPage permissions={[PermissionsKey.ManageBookingService]}>
                {withBookingFeatureGuard(<StoreBookingServicesOverView />)}
            </PermissionGate>
        </ProtectedRouteAdmin>
    },
    {
        path: "/admin/stores/:storeId/booking-services/create", element: <ProtectedRouteAdmin>
            <PermissionGate isPage permissions={[PermissionsKey.ManageBookingService]}>
                {withBookingFeatureGuard(<CreateBookingServicePage />)}
            </PermissionGate>
        </ProtectedRouteAdmin>
    },

    {
        path: "/admin/stores/:storeId/laundry-services", element: <ProtectedRouteAdmin>
            <PermissionGate isPage permissions={[PermissionsKey.ManageLaundryService]}>
                {withLaundryFeatureGuard(<StoreLaundryServicesOverView />)}
            </PermissionGate>
        </ProtectedRouteAdmin>
    },
    {
        path: "/admin/stores/:storeId/laundry-services/create", element: <ProtectedRouteAdmin>
            <PermissionGate isPage permissions={[PermissionsKey.ManageLaundryService]}>
                {withLaundryFeatureGuard(<CreateLaundryServicePage />)}
            </PermissionGate>
        </ProtectedRouteAdmin>
    },
    {
        path: "/admin/stores/:storeId/laundry-services/:serviceId", element: <ProtectedRouteAdmin>
            {withLaundryFeatureGuard(<LaundryServiceDetailsPage />)}
        </ProtectedRouteAdmin>
    },

    // admin activities page for store
    {
        path: "/admin/stores/:storeId/activity", element: <ProtectedRouteAdmin>
            <PermissionGate isPage permissions={[PermissionsKey.TrackStoreActivity]}>
                <ViewStoreActivityPage />
            </PermissionGate>
        </ProtectedRouteAdmin>
    },

    //admin orders pages  for store
    {
        path: "/admin/stores/:storeId/orders", element: <ProtectedRouteAdmin>
            <PermissionGate isPage permissions={[PermissionsKey.ViewStoreOrders]}>
                {withOrderFeatureGuard(<StoreOrdersOverView />)}
            </PermissionGate>
        </ProtectedRouteAdmin>
    },
    {
        path: "/admin/stores/:storeId/orders/:orderId", element: <ProtectedRouteAdmin>
            <PermissionGate isPage permissions={[PermissionsKey.ViewStoreOrders]}>
                {withOrderFeatureGuard(<OrderDetailsPage />)}
            </PermissionGate>
        </ProtectedRouteAdmin>
    },
    {
        path: "/admin/stores/:storeId/pos/carts/:cartId", element: <ProtectedRouteAdmin>
            {withPosFeatureGuard(
                <PosCartViewScreen
                    basePath="/admin/stores"
                    orderSuccessBasePath="/admin/stores"
                />
            )}
        </ProtectedRouteAdmin>
    },

    //  store statistics pages
    {
        path: "/admin/stores/:storeId/statistics", element: <ProtectedRouteAdmin>
            <PermissionGate isPage permissions={[PermissionsKey.ViewStoreStats]}>
                <OwnerHomePage />
            </PermissionGate>
        </ProtectedRouteAdmin>
    },

    // store variant categories pages for admin
    {
        path: "/admin/stores/:storeId/variant-categories", element: <ProtectedRouteAdmin>
            <PermissionGate isPage permissions={[PermissionsKey.ViewStoreVariant]}>
                {withOrderFeatureGuard(<StoreViewVariantCategoriesPage />)}
            </PermissionGate>
        </ProtectedRouteAdmin>
    },
    {
        path: "/admin/stores/:storeId/add-variantCategory", element: <ProtectedRouteAdmin>
            <PermissionGate isPage permissions={[PermissionsKey.CreateStoreVariant]}>
                {withOrderFeatureGuard(<StoreAddVariantCategoryPage />)}
            </PermissionGate>
        </ProtectedRouteAdmin>
    },

    // owenrs pages for admin
    {
        path: "/admin/owners", element: <ProtectedRouteAdmin>
            <PermissionGate isPage permissions={[PermissionsKey.ViewOwners]}>
                <AdminViewOwnersPage />
            </PermissionGate>
        </ProtectedRouteAdmin>
    },
    {
        path: "/admin/owners/:ownerId", element: <ProtectedRouteAdmin>
            <PermissionGate isPage permissions={[PermissionsKey.ViewOwners]}>
                <OnwerDetailsPageForAdmin />
            </PermissionGate>
        </ProtectedRouteAdmin>
    },

    {
        path: "/admin/owners/:ownerId/add-store/wizard", element: <ProtectedRouteAdmin>
            <PermissionGate isPage permissions={[PermissionsKey.CreateStore]}>
                <CreateStoreWizard />
            </PermissionGate>
        </ProtectedRouteAdmin>
    },

    // points pages 
    {
        path: "/admin/points", element: <ProtectedRouteAdmin>
            <PermissionGate isPage permissions={[PermissionsKey.SystemPoints]}>
                <PointsSystemAdminPage />
            </PermissionGate>
        </ProtectedRouteAdmin>
    },

    {
        path: "/admin/rewards-system", element: <ProtectedRouteAdmin>
            <PermissionGate isPage permissions={[PermissionsKey.SystemPoints]}>
                <RewardsSystemAdminPage />
            </PermissionGate>
        </ProtectedRouteAdmin>
    },

    // contact info for admin 
    {
        path: "/admin/contact-info", element: <ProtectedRouteAdmin>
            <PermissionGate isPage permissions={[PermissionsKey.SystemSettings]}>
                <ContactInfoAdminPage />
            </PermissionGate>
        </ProtectedRouteAdmin>
    },


    // roles pages for admin
    {
        path: "/admin/roles", element: <ProtectedRouteAdmin>
            <PermissionGate isPage permissions={[PermissionsKey.viewRole]}>
                <AdminViewRolesPage />
            </PermissionGate>
        </ProtectedRouteAdmin>
    },
    {
        path: "/admin/roles/:roleId", element: <ProtectedRouteAdmin>
            <PermissionGate isPage permissions={[PermissionsKey.viewRole]}>
                <RoleDetailsPage />
            </PermissionGate>
        </ProtectedRouteAdmin>
    },
    {
        path: "/admin/add-role", element: <ProtectedRouteAdmin>
            <PermissionGate isPage permissions={[PermissionsKey.CreateRole]}>
                <AdminAddRole />
            </PermissionGate>
        </ProtectedRouteAdmin>
    },
    {
        path: "/admin/roles/:roleId/update", element: <ProtectedRouteAdmin>
            <PermissionGate isPage permissions={[PermissionsKey.UpdateRole]}>
                <AdminUpdateRole />
            </PermissionGate>
        </ProtectedRouteAdmin>
    },


    //  employees pages for admin
    {
        path: "/admin/employees", element: <ProtectedRouteAdmin>
            <PermissionGate isPage permissions={[PermissionsKey.ViewAdmin]}>
                <AdminViewEmployees />
            </PermissionGate>
        </ProtectedRouteAdmin>
    },
    {
        path: "/admin/add-employee", element: <ProtectedRouteAdmin>
            <PermissionGate isPage permissions={[PermissionsKey.CreateAdmin]}>
                <AdminAddNewEmployee />
            </PermissionGate>
        </ProtectedRouteAdmin>
    },

    // customers pages for admin
    {
        path: "/admin/customers", element: <ProtectedRouteAdmin>
            <PermissionGate isPage permissions={[PermissionsKey.ViewCustomer]}>
                <AdminViewCustomersPage />
            </PermissionGate>
        </ProtectedRouteAdmin>
    },
    {
        path: "/admin/customers/:customerId", element: <ProtectedRouteAdmin>
            <PermissionGate isPage permissions={[PermissionsKey.ViewCustomer]}>
                <CustomerDetailsPage />
            </PermissionGate>
        </ProtectedRouteAdmin>
    },
    {
        path: "/admin/customers/bulk-add-balance",
        element: (
            <ProtectedRouteAdmin>
                <PermissionGate isPage permissions={[PermissionsKey.CustomerBalanceAction]}>
                    <AdminAddCustomerBalancePage />
                </PermissionGate>
            </ProtectedRouteAdmin>
        )
    },

    {
        path: "/admin/customers/:customerId/bookings/:bookingId", element: <ProtectedRouteAdmin>
            <PermissionGate isPage permissions={[PermissionsKey.ViewCustomerOrders]}>
                <StoreBookingDetailsPage />
            </PermissionGate>
        </ProtectedRouteAdmin>
    },

    {
        path: "/admin/customers/:customerId/orders/:orderId", element: <ProtectedRouteAdmin>
            <PermissionGate isPage permissions={[PermissionsKey.ViewCustomerOrders]}>
                <OrderDetailsPage />
            </PermissionGate>
        </ProtectedRouteAdmin>
    },


    // donations pages for admin
    {
        path: "/admin/donations", element: <ProtectedRouteAdmin>
            <PermissionGate isPage permissions={[PermissionsKey.ViewCustomer]}>
                <AdminDonationsPage />
            </PermissionGate>
        </ProtectedRouteAdmin>
    },

    // customer reviews pages for admin
    {
        path: "/admin/customer-reviews", element: <ProtectedRouteAdmin>
            <PermissionGate isPage permissions={[PermissionsKey.ViewStoreReviews]}>
                <AdminViewReviewsPage />
            </PermissionGate>
        </ProtectedRouteAdmin>
    },

    //   site content manage page
    {
        path: "/admin/site-content", element: <ProtectedRouteAdmin>
            <PermissionGate isPage permissions={[PermissionsKey.LandingPage]}>
                <SiteContentPage />
            </PermissionGate>
        </ProtectedRouteAdmin>
    },


    //     otp service logs
    {
        path: "/admin/otp-service", element: <ProtectedRouteAdmin>
            <PermissionGate isPage permissions={[PermissionsKey.ViewOtpHistory]}>
                <AdminViewOtpServiceLogs />
            </PermissionGate>
        </ProtectedRouteAdmin>
    },

    //   admins logs activities page
    {
        path: "/admin/employees/:employeeId/activities", element: <ProtectedRouteAdmin>
            <PermissionGate isPage permissions={[PermissionsKey.TrackAdminActivity]}>
                <EmployeeActivityPageLogs />
            </PermissionGate>
        </ProtectedRouteAdmin>
    },

    //   audit logs pages for admin
    {
        path: "/admin/audit-logs/:entity/:entityId", element: <ProtectedRouteAdmin>
            <PermissionGate isPage permissions={[PermissionsKey.ViewEntityAuditLogs]}>
                <AdminAuditLogsPage />
            </PermissionGate>
        </ProtectedRouteAdmin>
    },


    //  create owner page by admin
    {
        path: "/admin/add-owner", element: <ProtectedRouteAdmin>
            <PermissionGate isPage permissions={[PermissionsKey.CreateOwner]}>
                <AdminCreateOwnerPage />
            </PermissionGate>
        </ProtectedRouteAdmin>
    },

    // Notifications
    {
        path: "/admin/add-notification", element: <ProtectedRouteAdmin>
            <PermissionGate isPage permissions={[PermissionsKey.SendNotifications]}>
                <AdminCreateNotificationPage />
            </PermissionGate>
        </ProtectedRouteAdmin>
    },

    {
        path: "/admin/notifications", element: <ProtectedRouteAdmin>
            <PermissionGate isPage permissions={[PermissionsKey.SendNotifications]}>
                <AdminNotificationsListPage />
            </PermissionGate>
        </ProtectedRouteAdmin>
    },

    {
        path: "/admin/notification-tracking/:notificationId", element: <ProtectedRouteAdmin>
            <PermissionGate isPage permissions={[PermissionsKey.ViewNotifications]}>
                <AdminNotificationTrackingPage />
            </PermissionGate>
        </ProtectedRouteAdmin>
    },

    {
        path: "/admin/send-sms", element: <ProtectedRouteAdmin>
            <PermissionGate isPage permissions={[PermissionsKey.SendNotifications]}>
                <AdminSendSmsPage />
            </PermissionGate>
        </ProtectedRouteAdmin>
    },

    {
        path: "/admin/send-sms-bulk", element: <ProtectedRouteAdmin>
            <PermissionGate isPage permissions={[PermissionsKey.SendNotifications]}>
                <AdminSendSmsExcelPage />
            </PermissionGate>
        </ProtectedRouteAdmin>
    },

    {
        path: "/admin/notification-templates", element: <ProtectedRouteAdmin>
            <PermissionGate isPage permissions={[PermissionsKey.ManageNotificationTemplates]}>
                <AdminNotificationTemplatesPage />
            </PermissionGate>
        </ProtectedRouteAdmin>
    },

    {
        path: "/admin/social-links", element: <ProtectedRouteAdmin>
            <PermissionGate isPage permissions={[PermissionsKey.ManageSyestemEntities]}>
                <ViewSocialLinksPage />
            </PermissionGate>
        </ProtectedRouteAdmin>
    },

    {
        path: "/admin/add-social-link", element: <ProtectedRouteAdmin>
            <PermissionGate isPage permissions={[PermissionsKey.ManageSyestemEntities]}>
                <CreateSocialLinkPage />
            </PermissionGate>
        </ProtectedRouteAdmin>
    },

    {
        path: "/admin/custom-app", element: <ProtectedRouteAdmin>
            <PermissionGate isPage permissions={[PermissionsKey.ManageSyestemEntities]}>
                <CustomAppPage />
            </PermissionGate>
        </ProtectedRouteAdmin>
    },


    {
        path: "/admin/partners", element: <ProtectedRouteAdmin>
            <PermissionGate isPage permissions={[PermissionsKey.ManageSyestemEntities]}>
                <ViewPartnersPage />
            </PermissionGate>
        </ProtectedRouteAdmin>
    },

    {
        path: "/admin/add-partner", element: <ProtectedRouteAdmin>
            <PermissionGate isPage permissions={[PermissionsKey.ManageSyestemEntities]}>
                <AddPartnerPage />
            </PermissionGate>
        </ProtectedRouteAdmin>
    },

    // admin orgainzers pages
    {
        path: "/admin/organizers", element: <ProtectedRouteAdmin>
            <PermissionGate isPage permissions={[PermissionsKey.ViewOrganizer]}>
                <AdminViewOrganizersPage />
            </PermissionGate>
        </ProtectedRouteAdmin>
    },
    {
        path: "/admin/add-organizer", element: <ProtectedRouteAdmin>
            <PermissionGate isPage permissions={[PermissionsKey.CreateOrganizer]}>
                <AdminAddOrganizerPage />
            </PermissionGate>
        </ProtectedRouteAdmin>
    },

    // admin organizations pages
    {
        path: "/admin/organizations",
        element: <ProtectedRouteAdmin>
            <PermissionGate isPage permissions={[PermissionsKey.ManageOrganization]}>
                <AdminOrganizationsPage />
            </PermissionGate>
        </ProtectedRouteAdmin>
    },

    //  admin events pages
    {
        path: "/admin/events", element: <ProtectedRouteAdmin>
            <PermissionGate isPage permissions={[PermissionsKey.ViewEvent]}>
                <AdminViewEventsPage />
            </PermissionGate>
        </ProtectedRouteAdmin>
    },
    {
        path: "/admin/add-event", element: <ProtectedRouteAdmin>
            <PermissionGate isPage permissions={[PermissionsKey.CreateEvent]}>
                <AdminAddEventPage />
            </PermissionGate>
        </ProtectedRouteAdmin>
    },
    {
        path: "/admin/events/:eventId/edit", element: <ProtectedRouteAdmin>
            <PermissionGate isPage permissions={[PermissionsKey.UpdateEvent]}>
                <AdminUpdateEventPage />
            </PermissionGate>
        </ProtectedRouteAdmin>
    },
    {
        path: "/admin/events/:eventId/organizers/:organizerId/stores", element: <ProtectedRouteAdmin>
            <PermissionGate isPage permissions={[PermissionsKey.ViewEvent]}>
                <AdminEventStoresPage />
            </PermissionGate>
        </ProtectedRouteAdmin>
    },
    {
        path: "/admin/events/:eventId/organizers/:organizerId/owners", element: <ProtectedRouteAdmin>
            <PermissionGate isPage permissions={[PermissionsKey.ViewEvent]}>
                <AdminEventOwnersPage />
            </PermissionGate>
        </ProtectedRouteAdmin>
    },
]